<template>
  <!-- 单一根节点包裹（Vue2 限制）；弹窗自身渲染不受影响 -->
  <div class="autoreply-editor-root">
  <el-dialog
    :visible.sync="dialogVisible"
    width="560px"
    :before-close="handleCloseDialog"
    :close-on-click-modal="false"
  >
    <div slot="title" class="editor-dialog-title">
      <i :class="dialogTitleIcon"></i>
      <span>{{ title }}</span>
    </div>
    <!-- 消息序列 -->
    <div class="section-title">
      <span class="title-bar"></span>
      <span class="title-text">消息序列</span>
      <span class="title-hint">按顺序发送，共 {{ editing.length }}/{{ MAX_MSGS }} 条</span>
    </div>
    <div class="msg-list">
      <div v-for="(m, i) in editing" :key="i" class="msg-row">
        <span class="msg-seq">{{ i + 1 }}</span>
        <i :class="msgIcon(m)" class="msg-type-icon"></i>
        <span class="msg-preview" :title="msgPreview(m, true)">{{ msgPreview(m) }}</span>
        <div class="msg-actions">
          <el-button icon="el-icon-top" circle size="mini" :disabled="i === 0" @click="moveMsg(i, -1)"></el-button>
          <el-button icon="el-icon-bottom" circle size="mini" :disabled="i === editing.length - 1" @click="moveMsg(i, 1)"></el-button>
          <el-button icon="el-icon-delete" circle size="mini" @click="removeMsg(i)"></el-button>
        </div>
      </div>
      <div v-if="editing.length === 0" class="msg-empty">
        <i class="el-icon-chat-line-round"></i>
        <div>暂无消息，请从下方添加</div>
      </div>
    </div>

    <!-- 添加消息 -->
    <div class="msg-add">
      <div class="section-title">
        <span class="title-bar"></span>
        <span class="title-text">添加消息</span>
      </div>
      <div class="type-card-group">
        <div
          v-for="opt in addTypeOptions"
          :key="opt.value"
          class="type-card"
          :class="{ active: addType === opt.value }"
          @click="addType = opt.value"
        >
          <i :class="opt.icon"></i>
          <span>{{ opt.label }}</span>
        </div>
      </div>

      <!-- 文字 -->
      <div v-if="addType === 'text'" class="add-editor">
        <el-input
          type="textarea"
          v-model="textDraft"
          placeholder="请输入文字内容（最多 200 字）"
          maxlength="200"
          show-word-limit
          :rows="3"
        ></el-input>
        <el-button
          type="primary"
          plain
          size="small"
          :disabled="!textDraft.trim()"
          @click="appendText"
        >添加到话术</el-button>
      </div>

      <!-- 语音：录音/选择文件上传，就绪后点击“添加到话术”（≤60 秒） -->
      <div v-if="addType === 'voice'" class="add-editor">
        <voice-record-control
          ref="voiceRecordControl"
          :active="dialogVisible && addType === 'voice'"
          @send="onVoiceRecorded"
        ></voice-record-control>
        <div v-if="voiceDraft" class="draft-bar">
          <span class="draft-text"><i class="el-icon-microphone"></i>语音 {{ voiceDraft.duration }}″ 已就绪</span>
          <el-button type="primary" plain size="small" @click="appendVoice">添加到话术</el-button>
        </div>
        <div class="upload-tips">录音或选择文件，完成后点击“添加到话术”</div>
      </div>

      <!-- 视频：选择上传，就绪后点击“添加到话术”（≤25MB） -->
      <div v-if="addType === 'video'" class="add-editor">
        <el-upload
          class="video-uploader"
          action="action"
          accept="video/*"
          :show-file-list="false"
          :http-request="customUploadVideo"
          :before-upload="beforeVideoUpload"
          :auto-upload="true"
          :disabled="videoUploading"
        >
          <el-button
            size="small"
            type="primary"
            plain
            :loading="videoUploading"
            icon="el-icon-upload2"
          >{{ videoDraft ? '重新选择' : '选择视频' }}</el-button>
        </el-upload>
        <div v-if="videoDraft" class="draft-bar">
          <div class="draft-video">
            <img v-if="videoDraft.snapshotUrl" :src="videoDraft.snapshotUrl" alt="" @error="onMicroIconError">
            <span class="draft-text">{{ videoDraft.name || '视频' }}（{{ formatVideoSize(videoDraft.size) }}）已就绪</span>
          </div>
          <el-button type="primary" plain size="small" @click="appendVideo">添加到话术</el-button>
        </div>
        <div class="upload-tips">支持常见视频格式，不超过 25MB，上传后点击“添加到话术”</div>
      </div>

      <!-- 小程序：素材依赖微信 CDN 三要素（只能从消息收藏），选择后点击“添加到话术” -->
      <div v-if="addType === 'micro_program'" class="add-editor">
        <el-button
          size="small"
          type="primary"
          plain
          icon="el-icon-folder-opened"
          @click="microPickerVisible = true"
        >从素材库选择</el-button>
        <div v-if="microDraft" class="draft-bar">
          <div class="draft-micro">
            <img v-if="microDraft.icon" :src="microDraft.icon" alt="" @error="onMicroIconError">
            <span class="draft-text">{{ microDraft.title || '小程序素材' }} 已就绪</span>
          </div>
          <el-button type="primary" plain size="small" @click="appendMicro">添加到话术</el-button>
        </div>
        <div class="upload-tips">素材来源：小程序消息右键“设为快捷回复”收藏，选择后点击“添加到话术”</div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="cancelDialog">取 消</el-button>
      <el-button type="primary" :loading="saving" @click="confirmEditor">确 定</el-button>
    </span>
  </el-dialog>

  <!-- 小程序素材选择弹窗 -->
  <el-dialog
    class="micro-picker-dialog"
    :visible.sync="microPickerVisible"
    width="420px"
    append-to-body
  >
    <div slot="title" class="editor-dialog-title">
      <i class="el-icon-folder-opened"></i>
      <span>选择小程序素材</span>
    </div>
    <div v-if="microMaterials.length === 0" class="micro-empty">
      <i class="el-icon-folder-opened"></i>
      <div>素材库暂无可选小程序素材</div>
      <div class="micro-empty-sub">请先在小程序消息上右键“设为快捷回复”收藏</div>
    </div>
    <div v-else class="micro-list">
      <div v-for="(m, i) in microMaterials" :key="i" class="micro-item" @click="pickMicro(m)">
        <img v-if="cleanMicroIcon(microIcon(m))" :src="cleanMicroIcon(microIcon(m))" alt="" @error="onMicroIconError" />
        <div class="micro-info">
          <div class="micro-title">{{ microTitle(m) }}</div>
          <div class="micro-sub">{{ m.name || '小程序素材' }}</div>
        </div>
      </div>
    </div>
  </el-dialog>
  </div>
</template>

<script>
import { uploadImage, uploadFile } from '../../api/upload.js'
import { readVideoMeta, isVideoFile } from '../../utils/video.js'
import { msgIcon, msgPreview, parseElem, cleanMicroIcon, MAX_MSGS } from '../../utils/autoreplyMessages.js'
import VoiceRecordControl from './VoiceRecordControl.vue'

// 自动回复消息序列编辑器（弹窗）：新增/编辑两处（自动回复话术、超时跟进）共用。
// 确认后由父组件负责落库（saving 由父级传入控制按钮状态）；上传中拦截关闭/取消/提交
export default {
  name: 'AutoReplyMessagesEditor',
  components: { VoiceRecordControl },
  props: {
    title: { type: String, default: '编辑回复话术' },
    visible: { type: Boolean, default: false },
    messages: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false }
  },
  data() {
    return {
      MAX_MSGS,
      dialogVisible: false,
      editing: [],
      addType: 'text',
      // 添加消息类型选项（图标卡片）
      addTypeOptions: [
        { value: 'text', label: '文字', icon: 'el-icon-document' },
        { value: 'voice', label: '语音', icon: 'el-icon-microphone' },
        { value: 'video', label: '视频', icon: 'el-icon-video-camera' },
        { value: 'micro_program', label: '小程序', icon: 'el-icon-cpu' }
      ],
      textDraft: '',
      voiceDraft: null, // 已上传完成的语音（待点击“添加到话术”入序列）{ url, size, duration }
      videoDraft: null, // 已上传完成的视频（VideoElem 对象，待确认添加）
      microDraft: null, // 已选择的小程序素材（待确认添加）{ content, title, icon }
      videoUploading: false,
      microPickerVisible: false
    }
  },
  computed: {
    // 弹窗标题图标（添加/编辑语义区分）
    dialogTitleIcon() {
      return (this.title || '').indexOf('添加') > -1 ? 'el-icon-circle-plus-outline' : 'el-icon-edit-outline'
    },
    // 小程序素材库：已收藏为快捷回复的小程序消息（素材依赖微信 CDN 三要素，只能从消息收藏）
    microMaterials() {
      return (this.chatStore.quickReplies || []).filter(r => r.type === 'micro_program')
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) this.initFromProps()
    },
    dialogVisible(val) {
      if (val !== this.visible) this.$emit('update:visible', val)
    }
  },
  methods: {
    msgIcon,
    msgPreview,
    cleanMicroIcon,
    initFromProps() {
      this.editing = (this.messages || []).map(m => ({ ...m }))
      this.addType = 'text'
      this.textDraft = ''
      this.voiceDraft = null
      this.videoDraft = null
      this.microDraft = null
      this.videoUploading = false
      this.microPickerVisible = false
    },
    moveMsg(index, dir) {
      const target = index + dir
      if (target < 0 || target >= this.editing.length) return
      const list = this.editing
      const tmp = list[target]
      list[target] = list[index]
      list[index] = tmp
    },
    removeMsg(index) {
      this.editing.splice(index, 1)
    },
    // 素材上传中排队关闭（录音转码上传/视频截帧上传期间拦截）
    mediaUploading() {
      return this.videoUploading || !!(this.$refs.voiceRecordControl && this.$refs.voiceRecordControl.sending)
    },
    canAppend() {
      if (this.editing.length >= this.MAX_MSGS) {
        this.$message.warning(`最多添加 ${this.MAX_MSGS} 条消息`)
        return false
      }
      return true
    },
    appendText() {
      const text = (this.textDraft || '').trim()
      if (!text) return
      if (!this.canAppend()) return
      this.editing.push({ type: 'text', content: text })
      this.textDraft = ''
    },
    // 语音上传完成：暂存待确认（点击“添加到话术”才入序列）
    onVoiceRecorded(data) {
      this.voiceDraft = { url: data.url, size: data.size || 0, duration: data.duration }
    },
    // 确认添加语音到话术（与快捷回复同口径，VoiceElem JSON 作为消息内容）
    appendVoice() {
      if (!this.voiceDraft) return
      if (!this.canAppend()) return
      this.editing.push({
        type: 'voice',
        content: JSON.stringify({
          soundPath: this.voiceDraft.url,
          dataSize: this.voiceDraft.size,
          duration: this.voiceDraft.duration
        })
      })
      this.voiceDraft = null
      this.$message.success('语音已添加到话术')
    },
    // 视频上传前验证（与视频消息发送链路口径一致；≤25MB）
    beforeVideoUpload(file) {
      if (!isVideoFile(file)) {
        this.$message.error('仅支持视频文件（mp4/mov/avi/mkv/webm/flv/rmvb）')
        return false
      }
      if (!(file.size < 25 * 1024 * 1024)) {
        this.$message.error('视频大小不能超过 25MB!')
        return false
      }
      return true
    },
    // 视频素材上传（与快捷回复同链路）：本地截帧/读时长 → 上传封面+视频 → VideoElem JSON 加入话术
    customUploadVideo(options) {
      if (this.saving) {
        this.$message.warning('正在保存，请稍候')
        return
      }
      const file = options.file
      this.videoUploading = true
      readVideoMeta(file).then((meta) => {
        // 上传封面：截帧/上传失败均降级为空封面，不否决视频保存（与视频消息发送链路同策略）
        let snapshotPromise = Promise.resolve('')
        if (meta.snapshotBlob) {
          let snapshotFormData = new FormData()
          snapshotFormData.append('file', meta.snapshotBlob, 'snapshot.jpg')
          snapshotPromise = uploadImage(snapshotFormData).then((data) => data.path).catch(() => '')
        }
        return snapshotPromise.then((snapshotUrl) => ({ meta, snapshotUrl }))
      }).then(({ meta, snapshotUrl }) => {
        let formData = new FormData()
        formData.append('file', file)
        return uploadFile(formData).then((data) => ({ meta, snapshotUrl, data }))
      }).then(({ meta, snapshotUrl, data }) => {
        // 构造 VideoElem（与后端 msg_inner_struct.go 及视频消息发送链路字段对齐），暂存待确认
        this.videoDraft = {
          name: file.name,
          ext: data.ext,
          size: file.size,
          path: data.path,
          duration: meta.duration,
          snapshotUrl: snapshotUrl,
          snapshotWidth: meta.width,
          snapshotHeight: meta.height
        }
        this.$message.success('视频已上传，点击“添加到话术”完成添加')
      }).catch(() => {
        this.$message.error('视频上传失败，请重试')
      }).finally(() => {
        this.videoUploading = false
      })
    },
    // 确认添加视频到话术
    appendVideo() {
      if (!this.videoDraft) return
      if (!this.canAppend()) return
      this.editing.push({ type: 'video', content: JSON.stringify(this.videoDraft) })
      this.videoDraft = null
      this.$message.success('视频已添加到话术')
    },
    // 视频文件大小格式化（就绪预览）
    formatVideoSize(size) {
      if (!size) return '0KB'
      if (size >= 1024 * 1024) return (size / 1024 / 1024).toFixed(1) + 'MB'
      return Math.round(size / 1024) + 'KB'
    },
    microIcon(m) {
      return parseElem(m.content).weappIconUrl || ''
    },
    microTitle(m) {
      const d = parseElem(m.content)
      return d.title || d.appName || '小程序素材'
    },
    onMicroIconError(e) {
      e.target.style.display = 'none'
    },
    pickMicro(material) {
      // 选择后暂存待确认（点击“添加到话术”才入序列）；素材（MicroProgramElem JSON）原样复用，发送时与快捷回复同链路
      this.microDraft = {
        content: material.content,
        title: this.microTitle(material),
        icon: this.cleanMicroIcon(this.microIcon(material))
      }
      this.microPickerVisible = false
    },
    // 确认添加小程序素材到话术
    appendMicro() {
      if (!this.microDraft) return
      if (!this.canAppend()) return
      this.editing.push({ type: 'micro_program', content: this.microDraft.content })
      this.microDraft = null
      this.$message.success('小程序素材已添加到话术')
    },
    confirmEditor() {
      if (this.saving) return
      if (this.mediaUploading()) {
        this.$message.warning('素材上传中，请稍候')
        return
      }
      if (!this.editing.length) {
        this.$message.warning('请至少添加一条回复消息')
        return
      }
      this.$emit('confirm', this.editing.map(m => ({ ...m })))
    },
    cancelDialog() {
      if (this.saving) return
      if (this.mediaUploading()) {
        this.$message.warning('素材上传中，请稍候')
        return
      }
      this.dialogVisible = false
    },
    handleCloseDialog(done) {
      if (this.saving) return
      if (this.mediaUploading()) {
        this.$message.warning('素材上传中，请稍候')
        return
      }
      done()
    }
  }
}
</script>

<style lang="scss" scoped>
// 弹窗样式（两处弹窗共用；自带圆角，不依赖外部组件样式）
::v-deep .el-dialog {
  border-radius: 8px;

  .dialog-footer .el-button {
    min-width: 80px;
  }
}

// 弹窗标题（图标 + 文案）
.editor-dialog-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;

  i {
    margin-right: 8px;
    font-size: 18px;
    color: #67c23a;
    font-weight: normal;
  }
}

// 分区小标题（消息序列 / 添加消息）
.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: bold;
  color: #333;

  .title-bar {
    width: 3px;
    height: 14px;
    margin-right: 6px;
    border-radius: 2px;
    background: #67c23a;
  }

  .title-hint {
    margin-left: auto;
    font-size: 12px;
    font-weight: normal;
    color: #999;
  }
}

.msg-list {
  max-height: 240px;
  overflow-y: auto;
  margin-bottom: 12px;

  .msg-row {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    margin-bottom: 6px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: #b3e19d;
      box-shadow: 0 2px 6px rgba(103, 194, 58, 0.12);
    }

    .msg-seq {
      width: 18px;
      height: 18px;
      line-height: 18px;
      text-align: center;
      font-size: 11px;
      font-weight: 600;
      color: #67c23a;
      background: #f0f9eb;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .msg-type-icon {
      margin: 0 8px;
      color: #67c23a;
      font-size: 15px;
      flex-shrink: 0;
    }

    .msg-preview {
      flex: 1;
      min-width: 0;
      font-size: 13px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .msg-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;

      .el-button {
        margin-left: 0;
        padding: 4px;
      }
    }
  }

  .msg-empty {
    padding: 20px 16px;
    text-align: center;
    font-size: 12px;
    color: #999;
    background: #fafbfc;
    border: 1px dashed #e4e7ed;
    border-radius: 8px;

    i {
      display: block;
      margin-bottom: 6px;
      font-size: 24px;
      color: #c0c4cc;
    }
  }
}

.msg-add {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;

  .add-editor {
    margin-top: 10px;
    padding: 12px;
    background: #f7f8fa;
    border-radius: 8px;

    .el-button {
      margin-top: 8px;
    }

    // 素材就绪条（上传/选择完成后出现，点击“添加到话术”才入序列）
    .draft-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-top: 10px;
      padding: 7px 10px;
      background: #f0f9eb;
      border: 1px solid #e1f3d8;
      border-radius: 6px;

      .draft-text {
        display: inline-flex;
        align-items: center;
        min-width: 0;
        font-size: 12px;
        color: #529b2e;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        i {
          margin-right: 4px;
          flex-shrink: 0;
        }
      }

      .draft-video {
        display: flex;
        align-items: center;
        min-width: 0;

        img {
          width: 44px;
          height: 30px;
          margin-right: 6px;
          border-radius: 4px;
          object-fit: cover;
          background: #000;
          flex-shrink: 0;
        }
      }

      .draft-micro {
        display: flex;
        align-items: center;
        min-width: 0;

        img {
          width: 24px;
          height: 24px;
          margin-right: 6px;
          border-radius: 4px;
          object-fit: cover;
          flex-shrink: 0;
        }
      }

      .el-button {
        margin-top: 0;
        flex-shrink: 0;
      }
    }

    .upload-tips {
      margin-top: 6px;
      font-size: 12px;
      color: #999;
    }
  }
}

// 添加消息类型卡片组（与快捷回复弹窗同视觉）
.type-card-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.type-card {
  width: 64px;
  height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  i {
    font-size: 18px;
    color: #909399;
    transition: color 0.2s ease;
  }

  span {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1;
    color: #606266;
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  &:hover {
    border-color: #b3d8ff;
    background: #f5faff;

    i {
      color: #409EFF;
    }
  }

  &.active {
    border-color: #409EFF;
    background: #ecf5ff;

    i,
    span {
      color: #409EFF;
    }

    span {
      font-weight: 500;
    }
  }
}

.micro-list {
  max-height: 320px;
  overflow-y: auto;

  .micro-item {
    display: flex;
    align-items: center;
    padding: 8px;
    margin-bottom: 6px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &:hover {
      border-color: #409EFF;
      background: #f0f7ff;
    }

    img {
      width: 40px;
      height: 40px;
      border-radius: 6px;
      margin-right: 10px;
      flex-shrink: 0;
      object-fit: cover;
    }

    .micro-info {
      min-width: 0;

      .micro-title {
        font-size: 13px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .micro-sub {
        margin-top: 2px;
        font-size: 12px;
        color: #999;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.micro-empty {
  padding: 24px 20px;
  text-align: center;
  font-size: 13px;
  color: #999;
  line-height: 1.8;

  i {
    display: block;
    margin-bottom: 8px;
    font-size: 28px;
    color: #c0c4cc;
  }

  .micro-empty-sub {
    font-size: 12px;
    color: #c0c4cc;
  }
}
</style>
